import React from 'react';
import { Link } from 'react-router-dom';
import { Video, Film, Mic, Home, Users, Smartphone, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const services = [
  {
    id: '1',
    icon: Video,
    title: 'BRAND FILMS',
    description: 'Cinematic storytelling that captures your brand essence and drives emotional connection.',
    features: ['Creative Direction', 'Scriptwriting', 'Full Production']
  },
  {
    id: '2',
    icon: Film,
    title: 'COMMERCIAL PRODUCTION',
    description: 'High-impact commercials for healthcare, real estate, and professional services.',
    features: ['Multi-Camera Setup', 'Professional Crew', 'Premium Equipment']
  },
  {
    id: '3',
    icon: Mic,
    title: 'PODCAST VIDEO',
    description: 'Transform audio content into engaging visual experiences with dynamic editing.',
    features: ['Studio Design', 'Live Streaming', 'Motion Graphics']
  },
  {
    id: '4',
    icon: Home,
    title: 'PROPERTY SHOWCASE',
    description: 'Luxury real estate tours with drone cinematography that sell lifestyle.',
    features: ['Aerial Filming', 'Virtual Tours', '4K Quality']
  },
  {
    id: '5',
    icon: Users,
    title: 'CLIENT STORIES',
    description: 'Authentic testimonial videos that build trust and showcase transformations.',
    features: ['Interview Setup', 'Natural Lighting', 'Authentic Stories']
  },
  {
    id: '6',
    icon: Smartphone,
    title: 'SOCIAL CONTENT',
    description: 'Scroll-stopping short-form content optimized for Instagram, TikTok, YouTube.',
    features: ['Platform Optimization', 'Quick Turnaround', 'Viral Strategy']
  }
];

const ServicesAlt = () => {
  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-8xl lg:text-[12vw] font-black uppercase leading-none mb-8">
              WHAT WE
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                DO BEST
              </span>
            </h1>
            <p className="text-2xl font-semibold max-w-2xl">
              Full-service commercial filmmaking designed to drive measurable business results
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 group hover:bg-black transition-colors duration-300"
                >
                  <div className="w-16 h-16 bg-black group-hover:bg-white flex items-center justify-center mb-6 transition-colors">
                    <IconComponent className="text-white group-hover:text-black transition-colors" size={32} />
                  </div>
                  <h3 className="text-2xl font-black uppercase mb-4 group-hover:text-white transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-gray-300 mb-6 leading-relaxed transition-colors">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-sm font-bold uppercase tracking-wider text-gray-500 group-hover:text-gray-400 transition-colors">
                        • {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-screen-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-7xl lg:text-8xl font-black uppercase leading-none mb-20"
          >
            OUR PROCESS
          </motion.h2>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {[
              {
                num: '01',
                title: 'DISCOVERY',
                desc: 'Deep dive into your business goals, target audience, and brand vision'
              },
              {
                num: '02',
                title: 'PRE-PRODUCTION',
                desc: 'Scriptwriting, storyboarding, location scouting, and planning every detail'
              },
              {
                num: '03',
                title: 'PRODUCTION',
                desc: 'Professional filming with cinema-grade equipment and experienced crew'
              },
              {
                num: '04',
                title: 'POST-PRODUCTION',
                desc: 'Expert editing, color grading, sound design, and final delivery'
              }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-8xl font-black text-gray-100 mb-4">{phase.num}</div>
                <h3 className="text-3xl font-black uppercase mb-4">{phase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{phase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-screen-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-7xl lg:text-8xl font-black uppercase leading-none mb-16"
          >
            INDUSTRIES
            <br />
            <span className="text-gray-700">WE SERVE</span>
          </motion.h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {['HEALTHCARE', 'REAL ESTATE', 'PODCASTS', 'PROFESSIONAL SERVICES'].map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="aspect-square bg-white flex items-center justify-center p-8 hover:bg-orange-500 transition-colors group"
              >
                <h3 className="text-3xl font-black uppercase text-center leading-tight text-black group-hover:text-white transition-colors">
                  {industry}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-screen-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-6xl lg:text-8xl font-black uppercase leading-none mb-12">
              READY TO
              <br />
              GET STARTED?
            </h2>
            <Link
              to="/contact"
              className="inline-flex items-center bg-black text-white px-12 py-6 text-xl font-black uppercase hover:bg-gray-900 transition-colors group"
            >
              BOOK A CALL
              <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesAlt;